import { Link, useLoaderData } from 'react-router'
import { badgeVariants } from '#app/components/ui/badge.tsx'
import { Rail } from '#app/components/ui/rail.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import {
	getLocalizedField,
	type SupportedLocale,
} from '#app/utils/blog-i18n.ts'
import {
	generateBlogPostJsonLd,
	generateBlogPostMeta,
	generateBreadcrumbJsonLd,
} from '#app/utils/blog-seo.ts'
import { getPublishedPost, getRelatedPosts } from '#app/utils/blog.server.ts'
import { cache, cachified } from '#app/utils/cache.server.ts'
import { useLocalizedPath, useLang } from '#app/utils/i18n.ts'
import {
	renderMarkdown,
	estimateReadingTime,
} from '#app/utils/markdown.server.ts'
import { cn, getBlogImgSrc, getDomainUrl } from '#app/utils/misc.tsx'
import { BlogPostCard } from './+blog-components/blog-post-card.tsx'
import { BackLink } from './+marketing-components/back-link.tsx'
import { type Route } from './+types/blog.$slug.ts'

const translations = {
	backToBlog: {
		de: 'Zurück zum Blog',
		fr: 'Retour au blog',
		en: 'Back to blog',
	},
	relatedPosts: {
		de: 'Weitere Beiträge',
		fr: 'Articles similaires',
		en: 'Related posts',
	},
	previousPosts: {
		de: 'Vorherige Beiträge',
		fr: 'Articles précédents',
		en: 'Previous posts',
	},
	nextPosts: {
		de: 'Nächste Beiträge',
		fr: 'Articles suivants',
		en: 'Next posts',
	},
	readingTime: {
		de: (m: number) => `${m} Min. Lesezeit`,
		fr: (m: number) => `${m} min de lecture`,
		en: (m: number) => `${m} min read`,
	},
} as const

export async function loader({ params, request }: Route.LoaderArgs) {
	const post = await getPublishedPost(params.slug)

	if (!post) {
		throw new Response('Not found', { status: 404 })
	}

	const locale = (params.lang || 'de') as SupportedLocale
	const siteUrl = process.env.APP_URL || getDomainUrl(request)

	const content = getLocalizedField(post, 'content', locale)
	const readingTime = estimateReadingTime(content)

	const [contentHtml, relatedPosts] = await Promise.all([
		cachified({
			key: `blog:post:${post.slug}:html:${locale}:${post.updatedAt.getTime()}`,
			cache,
			ttl: 1000 * 60 * 60 * 24 * 7,
			swr: 1000 * 60 * 60 * 24 * 30,
			getFreshValue: () => renderMarkdown(content),
		}),
		getRelatedPosts(post.id, post.categoryId, 3),
	])

	return {
		post,
		contentHtml,
		readingTime,
		relatedPosts,
		siteUrl,
	}
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	if (!data) {
		return [{ title: '404 | Blog | Open Sourced' }]
	}

	const locale = (params.lang || 'de') as SupportedLocale

	const meta = generateBlogPostMeta({
		post: data.post,
		locale,
		siteUrl: data.siteUrl,
	})

	// JSON-LD structured data
	const jsonLd = generateBlogPostJsonLd({
		post: data.post,
		locale,
		siteUrl: data.siteUrl,
	})
	meta.push({ 'script:ld+json': jsonLd })

	// BreadcrumbList JSON-LD
	const breadcrumbJsonLd = generateBreadcrumbJsonLd({
		post: data.post,
		locale,
		siteUrl: data.siteUrl,
	})
	meta.push({ 'script:ld+json': breadcrumbJsonLd })

	return meta
}

export default function BlogPostDetail() {
	const { post, contentHtml, readingTime, relatedPosts } =
		useLoaderData<typeof loader>()
	const lang = useLang()
	const localePath = useLocalizedPath()
	const locale = lang as SupportedLocale

	const title = getLocalizedField(post, 'title', locale)

	const dateLocale = lang === 'de' ? 'de-CH' : lang === 'fr' ? 'fr-CH' : 'en-GB'

	return (
		<div className="pb-16">
			<article className="container max-w-3xl pt-32">
				{/* Back link */}
				<BackLink to={localePath('/blog')} className="mb-8">
					{translations.backToBlog[lang]}
				</BackLink>

				{/* Hero Image */}
				{post.heroImage && (
					<div className="mb-10 overflow-hidden rounded-xl shadow-md">
						<img
							src={getBlogImgSrc(post.heroImage.objectKey)}
							alt={post.heroImage.altText || title}
							className="aspect-video w-full object-cover"
						/>
					</div>
				)}

				{/* Title */}
				<h1 className="text-h2 mb-6 leading-tight font-bold">
					<WordReveal text={title} />
				</h1>

				{/* Meta bar */}
				<div className="border-border/50 text-body-sm text-muted-foreground mb-12 flex flex-wrap items-center gap-x-4 gap-y-2 border-b pb-8">
					<span className="text-foreground font-medium">
						{post.author.name || post.author.username}
					</span>

					{post.publishedAt && (
						<time dateTime={new Date(post.publishedAt).toISOString()}>
							{new Date(post.publishedAt).toLocaleDateString(dateLocale, {
								day: 'numeric',
								month: 'long',
								year: 'numeric',
							})}
						</time>
					)}

					{post.category && (
						<Link
							to={localePath(`/blog/category/${post.category.slug}`)}
							className={cn(badgeVariants({ variant: 'soft' }), 'focus-cosy')}
						>
							{getLocalizedField(post.category, 'name', locale)}
						</Link>
					)}

					<span>{translations.readingTime[lang](readingTime)}</span>
				</div>

				{/* Content */}
				<div
					className="blog-prose mx-auto mb-14 max-w-prose"
					dangerouslySetInnerHTML={{ __html: contentHtml }}
				/>

				{/* Tags */}
				{post.tags.length > 0 && (
					<div className="border-border/50 mb-16 border-t pt-8">
						<div className="flex flex-wrap gap-2">
							{post.tags.map((tag) => (
								<Link
									key={tag.id}
									to={localePath(`/blog/tag/${tag.slug}`)}
									className={cn(
										badgeVariants({ variant: 'soft' }),
										'focus-cosy',
									)}
								>
									{getLocalizedField(tag, 'name', locale)}
								</Link>
							))}
						</div>
					</div>
				)}
			</article>

			{/* Related Posts */}
			{relatedPosts.length > 0 && (
				<section>
					<div className="container">
						<div className="border-border/50 border-t" />
					</div>
					<Rail
						className="pt-12"
						heading={
							<h2 className="text-h4 font-semibold">
								{translations.relatedPosts[lang]}
							</h2>
						}
						previousLabel={translations.previousPosts[lang]}
						nextLabel={translations.nextPosts[lang]}
						items={relatedPosts.map((relatedPost) => ({
							key: relatedPost.id,
							node: (
								<BlogPostCard
									post={relatedPost}
									lang={lang}
									headingLevel="h3"
								/>
							),
						}))}
					/>
				</section>
			)}
		</div>
	)
}
